import queryString from 'query-string'
import * as React from 'react'
import { v4 as uuidV4 } from 'uuid'

export interface UtmParameter {
  utm_source?: string | null
  utm_medium?: string | null
  utm_campaign?: string | null
  utm_content?: string | null
  utm_term?: string | null
  utm_id?: string | null
}

export type InstantTourType = 'instant-tour-demo' | 'product-tour' | 'prioritization' | 'roadmaps' | 'objectives-okrs'

// export for <Layout /> component
export function useUtmTracking(): [string | undefined, UtmParameter] {
  return React.useMemo(() => {
    return typeof window === 'undefined' ? [undefined, {}] : [getAnonymousId(), getUtmParameter()]
  }, [typeof window])
}

// export for <Layout /> component
export function useGoogleAnalyticsClientId(): string | undefined {
  const [id, setId] = React.useState(getGoogleAnalyticsClientId())
  React.useEffect(() => {
    // google analytics might not be available right from the start
    const intervalId = window.setInterval(() => {
      if (!id) {
        const nextId = getGoogleAnalyticsClientId()
        if (nextId !== id) {
          setId(nextId)
        }
      }
    }, 1000)
    return () => window.clearInterval(intervalId)
  })
  return id
}

export function useTrackingLinkFactory(): (url: string) => string {
  const googleAnalyticsClientId = useGoogleAnalyticsClientId()
  const [anonymousId, utm] = useUtmTracking()
  const query = queryString.stringify({ ...utm, anonymousId, googleAnalyticsClientId })
  return React.useCallback((url) => url + (url.includes('?') ? '&' : '?') + query, [query])
}

export function useTrackingLink(url: string): string {
  const googleAnalyticsClientId = useGoogleAnalyticsClientId()
  const [anonymousId, utm] = useUtmTracking()
  const query = queryString.stringify({ ...utm, anonymousId, googleAnalyticsClientId })
  return url + (url.includes('?') ? '&' : '?') + query
}

export function useSignUpLink(): string {
  return useTrackingLink('https://app.airfocus.com/start')
}

export function useInstantTourLinkFactory(): (type?: InstantTourType) => string {
  const factory = useTrackingLinkFactory()
  return React.useCallback((type = 'product-tour') => factory(`https://airfocus.navattic.com/${type}/`), [factory])
}

function extractParam<T>(value: T | T[] | null | undefined): T | undefined {
  if (Array.isArray(value)) {
    return value[0]
  } else if (value) {
    return value
  } else {
    return undefined
  }
}

function extractUtmParameter(): UtmParameter | undefined {
  const params = queryString.parse(window.location.search)
  if (['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id'].some((p) => !!params[p])) {
    const utm: UtmParameter = {
      utm_source: extractParam(params.utm_source),
      utm_medium: extractParam(params.utm_medium),
      utm_campaign: extractParam(params.utm_campaign),
      utm_content: extractParam(params.utm_content),
      utm_term: extractParam(params.utm_term),
      utm_id: extractParam(params.utm_id),
    }
    return utm
  } else if (document.referrer.match(/https?:\/\/(www\.)?google/gi) || !!params.gclid) {
    return {
      utm_source: 'google',
      utm_medium: !!params.gclid ? 'cpc' : 'organic',
    }
  } else if (document.referrer) {
    return {
      utm_source: document.referrer,
      utm_medium: 'referral',
    }
  } else {
    return undefined
  }
}

// export for <HubspotForm /> component
export function getUtmParameter(): UtmParameter {
  const utm = extractUtmParameter()
  if (utm) {
    window.localStorage.setItem('utm', JSON.stringify(utm))
    return utm
  } else {
    try {
      const storedUtm = window.localStorage.getItem('utm')
      if (storedUtm) {
        return JSON.parse(storedUtm) as UtmParameter
      }
      // Return an empty object if there's nothing in localStorage
      return {}
    } catch {
      // Return an empty object if parsing fails
      return {}
    }
  }
}

function extractAnonymousId(): string | undefined {
  const params = queryString.parse(window.location.search)
  const anonymousId = extractParam(params.anonymousId)
  if (anonymousId) {
    return anonymousId
  } else {
    return undefined
  }
}

// export for <HubspotForm /> component
export function getAnonymousId(): string {
  const extractedId = extractAnonymousId()
  if (extractedId) {
    window.localStorage.setItem('anonymousId', extractedId)
    return extractedId
  } else {
    const storedId = window.localStorage.getItem('anonymousId')
    if (storedId) {
      return storedId
    } else {
      const randomId = uuidV4()
      window.localStorage.setItem('anonymousId', randomId)
      return randomId
    }
  }
}

function getGoogleAnalyticsClientId(): string | undefined {
  try {
    if (window.ga) {
      const tracker = window.ga.getAll()[0]
      return tracker ? (tracker.get('clientId') as string) : undefined
    }
    return undefined
  } catch {
    return undefined
  }
}

// Define the minimal shape of the analytics object that we need.
interface SafeAnalytics {
  identify: (userId: string, traits?: object, options?: object) => void
}

// This is a "type guard" function. It checks at runtime if an object
// is a valid `SafeAnalytics` object.
function isAnalytics(obj: any): obj is SafeAnalytics {
  return obj && typeof obj.identify === 'function'
}

export function identifyUser(anonymousId: string, utmParameter: UtmParameter, googleAnalyticsClientId?: string) {
  if (isAnalytics(window.analytics)) {
    window.analytics.identify(
      anonymousId,
      {
        utm_campaign: utmParameter.utm_campaign,
        utm_source: utmParameter.utm_source,
        utm_medium: utmParameter.utm_medium,
        utm_term: utmParameter.utm_term,
        utm_content: utmParameter.utm_content,
        googleAnalyticsClientId,
      },
      {
        integrations: {
          Intercom: false,
        },
      }
    )
  }
}
